import { Button, IconButton, List, styled } from "@mui/material";
import { Link } from "react-router-dom";

const Search = styled("div")`
  width: 100%;
  display: flex;
  align-items: center;
  position: relative;
  padding: 0px;

  @media (max-width: 560px) {
    padding: 0 0px;
  }
`;

const SearchButton = styled(IconButton)`
  background-color: transparent;
  width: 24px;
  height: 24px;
  min-width: 24px;
  border-radius: 20px;
  padding: 0;
  z-index: 10;

  &:hover {
    background-color: rgba(0, 0, 0, 0.08);
  }

  &:disabled {
    opacity: 0.5;
  }

  svg {
    width: 18px;
    height: 18px;
    color: #000000;
  }
`;

const ScanTop = styled("div")`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-direction: row;
  margin: 0;
  position: relative;
  padding: 0 24px;
  border-radius: 20px;
  background: #2b3501;
  height: 64px;
  box-sizing: border-box;

  @media (max-width: 560px) {
    padding: 0 18px;
  }
`;

const ScanTopText = styled("div")<{ hidden?: boolean }>`
  font-family: "Inter", sans-serif;
  font-weight: 700;
  font-size: 16px;
  line-height: 1.21;
  color: #ffffff;
  opacity: ${({ hidden }) => (hidden ? 0 : 1)};
  width: ${({ hidden }) => (hidden ? "0px" : "auto")};
  flex: ${({ hidden }) => (hidden ? 0 : 1)};
  transition: all 0.3s ease;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (max-width: 560px) {
    font-size: 14px;
    white-space: normal;
    line-height: 1.21;
  }
`;

// Mobile (below 560px) take on ScanTop (Figma 3002:4210): the whole bar is the white-paper link,
// its underlined label beside a chevron.
const ScanTopLink = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  height: 64px;
  box-sizing: border-box;
  padding: 0 18px 0 25px;
  border-radius: 20px;
  background: #2b3501;
  color: #ffffff;
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: normal;
  text-decoration: none;

  & > span {
    min-width: 0;
    text-decoration: underline;
    text-decoration-thickness: from-font;
    text-decoration-skip-ink: none;
    text-underline-position: from-font;
  }

  svg {
    flex-shrink: 0;
    font-size: 29px;
  }

  &:focus-visible {
    outline: 2px solid #ccff99;
    outline-offset: 2px;
  }
`;

// ScanTop's trailing chevron on desktop, matching ScanTopLink's: the same link as the label, so it
// stays out of the tab order (the label is the focusable one).
const ScanTopChevron = styled(Link)`
  display: flex;
  flex-shrink: 0;
  margin-left: 12px;
  color: #ffffff;

  svg {
    font-size: 29px;
  }
`;

const SearchContainer = styled("div")<{ expanded: boolean }>`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  transition: width 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  width: ${({ expanded }) => (expanded ? "100%" : "auto")};
  position: relative;
  overflow: hidden;
`;

const ScanMain = styled("div")`
  display: flex;
  flex-direction: column;
`;

const MobileWidgetGrid = styled("div")`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: repeat(3, 64px);
  grid-auto-flow: column;
  width: 100%;
  background-color: transparent;
`;

const MobileBankWidget = styled("div")`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 64px;
  padding: 0 18px;
  background-color: #959a80;
  border-radius: 20px;
  box-sizing: border-box;
  cursor: pointer;
`;

const MobileWidgetTextBlock = styled("div")`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  color: #000000;
`;

const MobileWidgetValue = styled("span")`
  font-family: "IBM Plex Mono", monospace;
  font-weight: 700;
  font-size: 14px;
  line-height: 16px;
  /* ReelNumber cells must match this line box or the reel grows the row. */
  --reel-cell: 16px;
`;

const MobileWidgetLabel = styled("span")`
  font-family: "IBM Plex Mono", monospace;
  font-weight: 400;
  font-size: 12px;
  line-height: 12px;
  text-transform: uppercase;
`;

const MarketCapIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24.0943 24.0943" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12.0472 24.0943C10.3806 24.0943 8.8145 23.7781 7.34876 23.1456C5.88303 22.5131 4.60804 21.6548 3.52379 20.5705C2.43955 19.4863 1.58119 18.2113 0.948713 16.7455C0.316238 15.2798 0 13.7137 0 12.0472C0 10.3806 0.316238 8.8145 0.948713 7.34876C1.58119 5.88303 2.43955 4.60804 3.52379 3.52379C4.60804 2.43955 5.88303 1.58119 7.34876 0.948713C8.8145 0.316238 10.3806 0 12.0472 0C13.7137 0 15.2798 0.316238 16.7455 0.948713C18.2113 1.58119 19.4863 2.43955 20.5705 3.52379C21.6548 4.60804 22.5131 5.88303 23.1456 7.34876C23.7781 8.8145 24.0943 10.3806 24.0943 12.0472C24.0943 13.7137 23.7781 15.2798 23.1456 16.7455C22.5131 18.2113 21.6548 19.4863 20.5705 20.5705C19.4863 21.6548 18.2113 22.5131 16.7455 23.1456C15.2798 23.7781 13.7137 24.0943 12.0472 24.0943ZM10.8424 21.6246V19.2754C10.1798 19.2754 9.61262 19.0395 9.14078 18.5677C8.66893 18.0958 8.43301 17.5286 8.43301 16.866V15.6613L2.65037 9.87866C2.59014 10.2401 2.53492 10.6015 2.48472 10.9629C2.43453 11.3243 2.40943 11.6857 2.40943 12.0472C2.40943 14.4767 3.20755 16.605 4.8038 18.4321C6.40005 20.2593 8.41293 21.3235 10.8424 21.6246ZM19.155 18.5526C19.9782 17.6491 20.6056 16.6401 21.0373 15.5258C21.469 14.4114 21.6849 13.2519 21.6849 12.0472C21.6849 10.0794 21.1377 8.28242 20.0434 6.65605C18.9492 5.02969 17.4884 3.85509 15.6613 3.13226V3.61415C15.6613 4.27674 15.4254 4.84396 14.9535 5.31581C14.4817 5.78765 13.9145 6.02358 13.2519 6.02358H10.8424V8.43301C10.8424 8.77434 10.727 9.06046 10.4961 9.29137C10.2652 9.52227 9.97906 9.63772 9.63772 9.63772H7.22829V12.0472H14.4566C14.7979 12.0472 15.084 12.1626 15.3149 12.3935C15.5458 12.6244 15.6613 12.9105 15.6613 13.2519V16.866H16.866C17.3881 16.866 17.8599 17.0216 18.2816 17.3328C18.7032 17.6441 18.9943 18.0506 19.155 18.5526Z"
      fill="black"
    />
  </svg>
);

const MintPriceIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24.889 24.7" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M24.7143 0.175195V12.3471C24.7142 15.7256 23.5191 18.6025 21.1342 20.9682C18.8468 23.2371 16.0858 24.4153 12.8598 24.5082L12.8764 24.5248H12.4516C10.9331 24.5248 9.50092 24.2678 8.15664 23.7602C6.86518 23.2725 5.69453 22.6009 4.63613 21.7436L2.19375 24.1674C2.06677 24.2933 1.93999 24.3818 1.8002 24.4389C1.66103 24.4957 1.51686 24.518 1.36465 24.518C1.04693 24.518 0.767864 24.3978 0.535547 24.1674C0.303408 23.937 0.182031 23.6595 0.182031 23.3432C0.182076 23.1917 0.204666 23.0479 0.262109 22.9096C0.319778 22.7708 0.408793 22.6457 0.535547 22.5199L2.97695 20.0971C2.11992 19.0486 1.43714 17.8882 0.945703 16.6078C0.433898 15.2742 0.175242 13.8535 0.175195 12.3471C0.175195 8.96838 1.36932 6.09074 3.7543 3.725C6.1393 1.35929 9.03972 0.175195 12.4447 0.175195H24.7143ZM12.4584 2.5248C9.70205 2.5248 7.36761 3.47987 5.43887 5.39297C3.51026 7.30597 2.54736 9.6203 2.54727 12.3529C2.54727 15.0857 3.51017 17.4008 5.43887 19.3139C7.29114 21.1511 9.5173 22.1025 12.1322 22.1752H12.4516C15.2078 22.1751 17.5415 21.2201 19.4701 19.307C21.3987 17.394 22.3616 15.0797 22.3617 12.3471V2.5248H12.4584ZM14.6146 5.46719C14.8113 5.46719 14.9825 5.54041 15.1273 5.68398L15.1342 5.68984L15.1391 5.69668C15.2493 5.82946 15.3241 5.97782 15.3334 6.15273C15.3426 6.32699 15.2845 6.47841 15.1781 6.61758L15.1791 6.61855L12.102 10.8021L17.2387 11.3061L17.2475 11.307L17.3793 11.3334C17.5069 11.3668 17.6214 11.4211 17.7191 11.4994C17.8504 11.6047 17.9409 11.7458 17.9936 11.9145C18.0455 12.0808 18.0543 12.2458 18.0102 12.4037C17.966 12.5613 17.873 12.6991 17.7426 12.8168L17.7406 12.8178L10.7865 18.9828L10.7367 19.0268H10.727C10.5995 19.1331 10.457 19.1928 10.2943 19.1928C10.1063 19.1928 9.94244 19.1161 9.80117 18.976C9.67172 18.8476 9.57188 18.695 9.56191 18.5072C9.55274 18.3326 9.61033 18.1799 9.71719 18.0404L12.8246 13.8568L7.69082 13.3539L7.68203 13.3529C7.50108 13.3262 7.34064 13.2649 7.21035 13.1605C7.07906 13.0553 6.98861 12.9142 6.93594 12.7455C6.88401 12.5791 6.87512 12.4142 6.91934 12.2562C6.96351 12.0986 7.05638 11.9609 7.18691 11.8432H7.18789L14.1088 5.67812C14.2523 5.53865 14.4209 5.46722 14.6146 5.46719Z"
      fill="#1F1F1F"
      stroke="black"
      strokeWidth="0.35"
    />
  </svg>
);

const TotalTxIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24.1548 24.0341" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M8.2793 0.71875V7.61523H6.21387V4.13477L5.92969 4.3623C4.80245 5.26409 3.90544 6.3761 3.24023 7.69629C2.57324 9.02012 2.2403 10.4612 2.24023 12.0166C2.24023 14.5345 3.06602 16.7154 4.71582 18.5498C6.32939 20.3438 8.32476 21.4059 10.6943 21.7383V23.835C9.25869 23.6621 7.91949 23.2606 6.67676 22.625C5.37705 21.9602 4.2468 21.0978 3.28516 20.0371C2.32312 18.9759 1.56395 17.7601 1.00781 16.3896C0.453081 15.0226 0.174805 13.5652 0.174805 12.0166C0.174862 10.2092 0.537031 8.54429 1.25977 7.01953C1.98438 5.49088 2.972 4.18181 4.22168 3.09082L4.57324 2.78418H1.38281V0.71875H8.2793ZM13.46 0.196289C16.3923 0.528118 18.8652 1.78807 20.8857 3.9834C22.9486 6.22478 23.9794 8.89935 23.9795 12.0166C23.9795 13.8242 23.6174 15.4897 22.8945 17.0146C22.1699 18.5432 21.1832 19.8524 19.9336 20.9434L19.582 21.25H22.7725V23.3154H15.876V16.4189H17.9414V19.9043L18.2266 19.6709C19.3527 18.7495 20.249 17.6329 20.9141 16.3232C21.5814 15.009 21.9141 13.5722 21.9141 12.0166C21.914 9.49874 21.0884 7.31774 19.4385 5.4834C17.8248 3.68955 15.8296 2.62713 13.46 2.29492V0.196289ZM18.6543 9.02637L10.3564 17.3242L5.5 12.4395L6.94336 10.9961L10.2324 14.2842L10.3564 14.4082L10.4805 14.2842L17.209 7.55469L18.6543 9.02637Z"
      fill="#1F1F1F"
      stroke="black"
      strokeWidth="0.35"
    />
  </svg>
);

const Tr3MintedIcon = () => (
  <svg width="24.8069" height="27.2876" viewBox="0 0 24.8069 27.2876" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M0 6.20173V0H6.20173V2.48069H2.48069V6.20173H0ZM22.3262 6.20173V2.48069H18.6052V0H24.8069V6.20173H22.3262ZM0 27.2876V21.0859H2.48069V24.8069H6.20173V27.2876H0ZM18.6052 27.2876V24.8069H22.3262V21.0859H24.8069V27.2876H18.6052ZM6.20173 21.0859H18.6052V6.20173H6.20173V21.0859ZM6.20173 23.5666C5.51954 23.5666 4.93554 23.3237 4.44974 22.8379C3.96394 22.3521 3.72104 21.7681 3.72104 21.0859V6.20173C3.72104 5.51954 3.96394 4.93554 4.44974 4.44974C4.93554 3.96394 5.51954 3.72104 6.20173 3.72104H18.6052C19.2874 3.72104 19.8714 3.96394 20.3572 4.44974C20.843 4.93554 21.0859 5.51954 21.0859 6.20173V21.0859C21.0859 21.7681 20.843 22.3521 20.3572 22.8379C19.8714 23.3237 19.2874 23.5666 18.6052 23.5666H6.20173ZM8.68242 11.1631H16.1245V8.68242H8.68242V11.1631ZM8.68242 14.8841H16.1245V12.4035H8.68242V14.8841ZM8.68242 18.6052H16.1245V16.1245H8.68242V18.6052Z"
      fill="#1F1F1F"
    />
  </svg>
);

const TotalEftsIcon = () => (
  <svg width="26.4278" height="29.3776" viewBox="0 0 26.4278 29.3776" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M13.2141 0.221747L0.195 7.27046V21.7009L13.2141 29.1529L26.2328 21.7009V7.27046L13.2141 0.221747ZM24.0676 20.4384L14.301 26.0406V22.3936C13.8363 22.4725 13.363 22.525 12.8721 22.525C12.6178 22.525 12.3723 22.5075 12.1268 22.4812V26.0406L2.36021 20.4384V9.69884L5.63928 11.6364C5.91983 10.9701 6.29662 10.3477 6.76127 9.76904L3.55284 7.88411L13.2051 2.74665L22.8578 7.88411L20.6922 9.15529V11.6802L24.05 9.69017V20.4298L24.0676 20.4384Z"
      fill="black"
      stroke="black"
      strokeWidth="0.39"
      strokeMiterlimit="10"
    />
    <path
      d="M9.02322 10.8289C7.96241 11.8897 7.42778 13.1785 7.42778 14.6864C7.42778 15.2475 7.5155 15.791 7.69961 16.3083C7.88371 16.8343 8.12926 17.3253 8.42734 17.7812L7.79592 18.4124C7.62058 18.5877 7.54165 18.7893 7.54165 19.0173C7.54165 19.2452 7.62935 19.4469 7.79592 19.6222C7.97127 19.7976 8.17286 19.8765 8.4008 19.8677C8.62874 19.859 8.83033 19.78 9.00567 19.6222L9.61054 19.0524C10.1103 19.3767 10.6278 19.6398 11.1713 19.8414C11.7149 20.043 12.2845 20.1395 12.8806 20.1395C14.3885 20.1395 15.6776 19.6047 16.7385 18.5439C17.7993 17.4831 18.3339 16.1943 18.3339 14.6864V9.23334H12.8806C11.3727 9.23334 10.084 9.76808 9.02322 10.8289ZM16.6066 14.6864C16.6066 15.7209 16.2474 16.6064 15.5197 17.3253C14.7921 18.0529 13.9151 18.4124 12.8806 18.4124C12.5212 18.4124 12.1705 18.351 11.8374 18.2283C11.5043 18.1056 11.1712 17.9652 10.8468 17.8162L14.1169 14.5461C14.2922 14.3708 14.3712 14.1692 14.3712 13.9324C14.3712 13.6957 14.2835 13.4853 14.1169 13.3187C13.9416 13.1434 13.74 13.0645 13.512 13.0645C13.2841 13.0645 13.0825 13.1521 12.9072 13.3187L9.61054 16.6151C9.47904 16.2907 9.36517 15.9664 9.2775 15.6507C9.18983 15.3351 9.14608 15.0108 9.14608 14.6864C9.14608 13.6519 9.5053 12.7664 10.233 12.0475C10.9606 11.3199 11.8376 10.9604 12.8721 10.9604H16.598V14.6864H16.6066Z"
      fill="black"
      stroke="black"
      strokeWidth="0.39"
      strokeMiterlimit="10"
    />
  </svg>
);

const TillHalvingIcon = () => (
  <svg width="25" height="25" viewBox="0 0 24.9963 24.9963" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12.4981 19.997C14.5812 19.997 16.3517 19.268 17.8098 17.8098C19.268 16.3517 19.997 14.5812 19.997 12.4981C19.997 10.4151 19.268 8.64454 17.8098 7.18643C16.3517 5.72831 14.5812 4.99925 12.4981 4.99925V12.4981L7.18643 17.8098C7.91549 18.4972 8.73307 19.0336 9.63919 19.419C10.5453 19.8043 11.4983 19.997 12.4981 19.997ZM12.4981 24.9963C10.7692 24.9963 9.14447 24.6682 7.62386 24.012C6.10326 23.3559 4.78054 22.4654 3.6557 21.3406C2.53087 20.2157 1.64038 18.893 0.984228 17.3724C0.328076 15.8518 0 14.227 0 12.4981C0 10.7692 0.328076 9.14447 0.984228 7.62386C1.64038 6.10326 2.53087 4.78054 3.6557 3.6557C4.78054 2.53087 6.10326 1.64038 7.62386 0.984228C9.14447 0.328076 10.7692 0 12.4981 0C14.227 0 15.8518 0.328076 17.3724 0.984228C18.893 1.64038 20.2157 2.53087 21.3406 3.6557C22.4654 4.78054 23.3559 6.10326 24.012 7.62386C24.6682 9.14447 24.9963 10.7692 24.9963 12.4981C24.9963 14.227 24.6682 15.8518 24.012 17.3724C23.3559 18.893 22.4654 20.2157 21.3406 21.3406C20.2157 22.4654 18.893 23.3559 17.3724 24.012C15.8518 24.6682 14.227 24.9963 12.4981 24.9963ZM12.4981 22.4966C15.2894 22.4966 17.6536 21.528 19.5908 19.5908C21.528 17.6536 22.4966 15.2894 22.4966 12.4981C22.4966 9.70689 21.528 7.34265 19.5908 5.40544C17.6536 3.46823 15.2894 2.49963 12.4981 2.49963C9.70689 2.49963 7.34265 3.46823 5.40544 5.40544C3.46823 7.34265 2.49963 9.70689 2.49963 12.4981C2.49963 15.2894 3.46823 17.6536 5.40544 19.5908C7.34265 21.528 9.70689 22.4966 12.4981 22.4966Z"
      fill="#1F1F1F"
    />
  </svg>
);

const StyledList = styled(List)`
  width: 100%;
  overflow-x: auto;
  background-color: ${({ theme }) => theme.palette.back?.table};
  & > li:last-of-type {
    margin-bottom: 0;
  }
`;

const BackButton = styled(Button)`
  padding: 0;
  margin: 24px 0;
`;

export {
  BackButton,
  ScanMain,
  Search,
  SearchButton,
  ScanTop,
  StyledList,
  ScanTopText,
  ScanTopLink,
  ScanTopChevron,
  SearchContainer,
  MobileWidgetGrid,
  MobileBankWidget,
  MobileWidgetTextBlock,
  MobileWidgetValue,
  MobileWidgetLabel,
  MarketCapIcon,
  MintPriceIcon,
  TotalTxIcon,
  Tr3MintedIcon,
  TotalEftsIcon,
  TillHalvingIcon,
};
